import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faChartLine, faChartPie, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';

// Semantic, meaning-based colors instead of arbitrary color names — a shop
// owner shouldn't have to know what "emerald" means, just that green = good/sales.
const THEME = {
  sales:    { bar: 'bg-gradient-to-t from-emerald-600 to-emerald-400', line: '#34d399', dot: '#34d399', text: 'text-emerald-400' },
  purchase: { bar: 'bg-gradient-to-t from-rose-600 to-rose-400',       line: '#fb7185', dot: '#fb7185', text: 'text-rose-400' },
  receipts: { bar: 'bg-gradient-to-t from-cyan-600 to-cyan-400',       line: '#22d3ee', dot: '#22d3ee', text: 'text-cyan-400' },
  neutral:  { bar: 'bg-gradient-to-t from-violet-600 to-violet-400',   line: '#a78bfa', dot: '#a78bfa', text: 'text-violet-400' },
};

const PIE_COLORS = ['#22d3ee', '#34d399', '#fb7185', '#fbbf24', '#a78bfa', '#60a5fa'];

const fmtRupee = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const EmptyState = ({ icon, title }) => (
  <div className="bg-gray-800 rounded-3xl p-6">
    <h3 className="text-base lg:text-lg font-semibold text-cyan-200 mb-3 flex items-center gap-2">
      <FontAwesomeIcon icon={icon} /> {title}
    </h3>
    <p className="text-gray-500 text-sm">Nothing to show yet — once you record a few entries, this chart will fill in automatically.</p>
  </div>
);

// Small "at a glance" strip so a non-technical reader gets the headline
// number before they even have to read the chart.
const SummaryChip = ({ label, value, tone }) => (
  <div className="bg-gray-900 rounded-xl px-3 py-2 text-center flex-1 min-w-[90px]">
    <div className="text-[10px] lg:text-xs text-gray-500">{label}</div>
    <div className={`text-sm lg:text-lg font-bold ${tone}`}>{value}</div>
  </div>
);

// ================= BAR CHART =================
// theme: 'sales' | 'purchase' | 'receipts' | 'neutral'
export const BarChart = ({ data, title, theme = 'neutral' }) => {
  if (!data || data.length === 0) return <EmptyState icon={faChartBar} title={title} />;

  const t = THEME[theme] || THEME.neutral;
  const values = data.map(d => d.value);
  const maxValue = Math.max(...values, 1);
  const total = values.reduce((s, v) => s + v, 0);
  const avg = total / values.length;
  const best = data.reduce((a, b) => (b.value > a.value ? b : a), data[0]);

  // trend: compare last point to the one before it, so the reader gets an
  // instant up/down sense without doing any math themselves.
  const trendUp = data.length > 1 && data[data.length - 1].value >= data[data.length - 2].value;

  const gridLines = [0.25, 0.5, 0.75, 1]; // 4 horizontal reference lines

  return (
    <div className="bg-gray-800 rounded-3xl p-4 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-base lg:text-lg font-semibold text-cyan-200 flex items-center gap-2">
          <FontAwesomeIcon icon={faChartBar} /> {title}
        </h3>
        {data.length > 1 && (
          <span className={`text-xs flex items-center gap-1 ${trendUp ? 'text-emerald-400' : 'text-rose-400'}`}>
            <FontAwesomeIcon icon={trendUp ? faArrowUp : faArrowDown} />
            {trendUp ? 'Trending up' : 'Trending down'}
          </span>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <SummaryChip label="Total" value={fmtRupee(total)} tone={t.text} />
        <SummaryChip label="Average" value={fmtRupee(Math.round(avg))} tone="text-gray-300" />
        <SummaryChip label="Best" value={best.label} tone="text-gray-300" />
      </div>

      <div className="relative h-56 lg:h-64 pl-2">
        {/* Reference gridlines with value labels, so the height of a bar means something concrete */}
        <div className="absolute inset-0 flex flex-col justify-between text-[10px] text-gray-600 pointer-events-none">
          {gridLines.slice().reverse().map((g, i) => (
            <div key={i} className="flex items-center gap-1 border-b border-gray-700/50 last:border-b-0">
              <span className="w-8 text-right pr-1">{Math.round(maxValue * g)}</span>
            </div>
          ))}
        </div>

        <div className="absolute inset-0 flex items-end gap-2 lg:gap-3 pl-9">
          {data.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center gap-1 group h-full justify-end">
              <div className="text-[10px] lg:text-xs text-gray-300 font-semibold">{fmtRupee(item.value)}</div>
              <div
                title={`${item.label}: ${fmtRupee(item.value)}`}
                className={`w-full rounded-t-lg transition-all duration-300 group-hover:brightness-110 cursor-default ${t.bar}`}
                style={{ height: `${Math.max((item.value / maxValue) * 100, 3)}%` }}
              />
              <div className="text-[10px] lg:text-xs text-gray-500 text-center mt-1 truncate w-full">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================= LINE CHART =================
export const LineChart = ({ data, title, theme = 'receipts' }) => {
  if (!data || data.length === 0) return <EmptyState icon={faChartLine} title={title} />;

  const t = THEME[theme] || THEME.receipts;
  const values = data.map(d => d.value);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values, 0);
  const range = maxValue - minValue || 1;
  const total = values.reduce((s, v) => s + v, 0);

  const points = data.map((item, index) => {
    const x = data.length === 1 ? 50 : (index / (data.length - 1)) * 100;
    const y = 100 - ((item.value - minValue) / range) * 100;
    return { x, y, ...item };
  });
  const polyPoints = points.map(p => `${p.x},${p.y}`).join(' ');
  const areaPoints = `0,100 ${polyPoints} 100,100`;

  return (
    <div className="bg-gray-800 rounded-3xl p-4 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-base lg:text-lg font-semibold text-cyan-200 flex items-center gap-2">
          <FontAwesomeIcon icon={faChartLine} /> {title}
        </h3>
        <span className="flex items-center gap-1.5 text-xs text-gray-400">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.dot }} />
          Total {fmtRupee(total)}
        </span>
      </div>

      <div className="relative h-56 lg:h-64">
        <div className="absolute left-0 top-0 text-[10px] text-gray-500">{fmtRupee(maxValue)}</div>
        <div className="absolute left-0 bottom-6 text-[10px] text-gray-500">{fmtRupee(minValue)}</div>

        <svg className="w-full h-[calc(100%-24px)]" viewBox="0 0 100 100" preserveAspectRatio="none">
          {[20, 40, 60, 80].map((y, i) => (
            <line key={i} x1="0" y1={y} x2="100" y2={y} stroke="#374151" strokeWidth="0.5" />
          ))}
          <polygon points={areaPoints} fill={t.dot} opacity="0.12" />
          <polyline points={polyPoints} fill="none" stroke={t.line} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {points.map((p, index) => (
            <circle key={index} cx={p.x} cy={p.y} r="2" fill={t.line} stroke="#1f2937" strokeWidth="0.7">
              <title>{`${p.label}: ${fmtRupee(p.value)}`}</title>
            </circle>
          ))}
        </svg>

        <div className="flex justify-between text-[10px] lg:text-xs text-gray-500 mt-2">
          {data.map((item, i) => (
            <div key={i} className="text-center flex-1 truncate">{item.label}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================= DONUT / SPLIT CHART =================
// Ideal for "how are people paying" (Cash vs UPI vs Card vs Credit) —
// a shape most people read instantly without needing to understand axes.
export const DonutChart = ({ data, title }) => {
  if (!data || data.length === 0) return <EmptyState icon={faChartPie} title={title} />;

  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let cumulative = 0;
  const segments = data.map((d, i) => {
    const fraction = d.value / total;
    const start = cumulative;
    cumulative += fraction;
    return { ...d, start, end: cumulative, color: PIE_COLORS[i % PIE_COLORS.length] };
  });

  const toXY = (frac) => {
    const angle = frac * 2 * Math.PI - Math.PI / 2;
    return [50 + 40 * Math.cos(angle), 50 + 40 * Math.sin(angle)];
  };

  return (
    <div className="bg-gray-800 rounded-3xl p-4 lg:p-6">
      <h3 className="text-base lg:text-lg font-semibold text-cyan-200 mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faChartPie} /> {title}
      </h3>

      <div className="flex flex-col sm:flex-row items-center gap-6">
        <svg viewBox="0 0 100 100" className="w-36 h-36 lg:w-44 lg:h-44 shrink-0">
          {segments.map((seg, i) => {
            const [x1, y1] = toXY(seg.start);
            const [x2, y2] = toXY(seg.end);
            const largeArc = seg.end - seg.start > 0.5 ? 1 : 0;
            const path = `M50,50 L${x1},${y1} A40,40 0 ${largeArc} 1 ${x2},${y2} Z`;
            return <path key={i} d={path} fill={seg.color} stroke="#1f2937" strokeWidth="0.5">
              <title>{`${seg.label}: ${fmtRupee(seg.value)} (${Math.round((seg.value / total) * 100)}%)`}</title>
            </path>;
          })}
          <circle cx="50" cy="50" r="22" fill="#1f2937" />
          <text x="50" y="47" textAnchor="middle" fontSize="8" fill="#9ca3af">Total</text>
          <text x="50" y="58" textAnchor="middle" fontSize="9" fill="#e5e7eb" fontWeight="bold">{fmtRupee(total)}</text>
        </svg>

        {/* Legend — color swatch + label + amount, no need to interpret angles */}
        <div className="flex-1 w-full space-y-2">
          {segments.map((seg, i) => (
            <div key={i} className="flex items-center justify-between text-xs lg:text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                <span className="text-gray-300">{seg.label}</span>
              </div>
              <div className="text-gray-400">
                {fmtRupee(seg.value)} <span className="text-gray-600">({Math.round((seg.value / total) * 100)}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
